/**
 * Delivery notes as one compact table (was one tall card per DN): newest delivery first, DN-number
 * search, Destination / Project filters, a "Supplier missing on N of M" filter, '—' for empty cells,
 * "1 item" / "5 items", the count from the API total and a pager. A row opens its items in place.
 * On phones the same data is a two-line list.
 */
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronRight, CloudOff, FolderOpen, ListTree, SearchX, TriangleAlert, Truck, Warehouse, HardHat } from 'lucide-react';
import api from '@/lib/api';
import { getErrorMessage } from '@/lib/errors';
import { formatDate, formatNumber } from '@/lib/format';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { RowActions } from '@/components/ui/row-actions';
import {
  CardFoot, CardHead, ChipSelect, DateCell, Dash, DestinationTag, Pager, SearchField, SfCard, TableScroll,
  numClass, plural, tableClass, tdClass, thClass, useIsPhone,
} from './SfUi';

const DESTINATIONS = [
  { value: 'to_stock', label: 'Warehouse' },
  { value: 'to_site', label: 'Direct to site' },
];

function useDebounced(value, delay = 250) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

function DnItems({ dn }) {
  const lines = Array.isArray(dn.line_items) ? dn.line_items : [];
  return (
    <div className="grid gap-4 border-l-[3px] border-brand bg-surface-2 px-4 py-3 sm:grid-cols-[minmax(0,1fr)_240px] sm:px-[18px]">
      <div className="min-w-0">
        <p className="mb-1.5 flex justify-between text-[11px] font-bold uppercase tracking-[0.06em] text-ink-3"><span>Items</span><span>Delivered</span></p>
        {lines.length ? (
          <ul className="space-y-1">
            {lines.map((line, index) => (
              <li key={`${line.part_number}-${index}`} className="flex min-w-0 items-baseline gap-3 text-[12.5px]">
                <span className="shrink-0 font-mono text-[12px] text-ink">{line.part_number || '—'}</span>
                <span className="min-w-0 flex-1 truncate text-ink-3">{line.description}</span>
                <span className="shrink-0 font-semibold tabular-nums text-ink">{formatNumber(line.delivered_qty)}</span>
              </li>
            ))}
          </ul>
        ) : <p className="text-[12.5px] text-ink-3">No items were recorded on this delivery note.</p>}
      </div>
      <dl className="grid grid-cols-[auto_1fr] content-start gap-x-3 gap-y-1 text-[12.5px]">
        <dt className="text-ink-3">Received by</dt><dd className="text-ink">{dn.received_by || <Dash />}</dd>
        {dn.sub_boq ? <><dt className="text-ink-3">Sub-BOQ</dt><dd className="text-ink">{dn.sub_boq}</dd></> : null}
        {dn.notes ? <><dt className="text-ink-3">Notes</dt><dd className="text-ink">{dn.notes}</dd></> : null}
      </dl>
    </div>
  );
}

/** The table's filters as /delivery-notes query params (no paging) — what an export of "this view" sends. */
export function deliveryNoteFilterParams({ search, destination, projectId, supplierMissing }) {
  const params = {};
  if (search) params.search = search;
  if (destination) params.destination = destination;
  if (projectId) params.project_id = projectId;
  if (supplierMissing) params.supplier_missing = true;
  return params;
}

/**
 * props: pageSize, title, variant ('recent' = Overview card with "View all"; 'full' = Deliveries tab),
 * projects (for the Project chip), refreshKey (bump to refetch), onViewAll, testId,
 * onFiltersChange(params) — called with the active filters as query params (the page's Export uses them).
 */
export function DeliveryNotesTable({ pageSize = 8, title = 'Recent deliveries', variant = 'recent', projects = [], refreshKey = 0, onViewAll, onFiltersChange, testId = 'dn-list' }) {
  const navigate = useNavigate();
  const isPhone = useIsPhone();
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const search = useDebounced(searchInput.trim());
  const [destination, setDestination] = useState(null);
  const [projectId, setProjectId] = useState(null);
  const [supplierMissing, setSupplierMissing] = useState(false);
  const [state, setState] = useState({ items: [], total: 0, totalPages: 1, counts: null, loading: true, error: '' });
  const [openId, setOpenId] = useState(null);
  const requestRef = useRef(0);

  useEffect(() => { setPage(1); }, [search, destination, projectId, supplierMissing]);
  const filterParams = useMemo(
    () => deliveryNoteFilterParams({ search, destination, projectId, supplierMissing }),
    [search, destination, projectId, supplierMissing]
  );
  const filtersChangeRef = useRef(onFiltersChange);
  filtersChangeRef.current = onFiltersChange;
  useEffect(() => { filtersChangeRef.current?.(filterParams); }, [filterParams]);

  const load = useCallback(async () => {
    const requestId = requestRef.current + 1;
    requestRef.current = requestId;
    setState((prev) => ({ ...prev, loading: true, error: '' }));
    try {
      const params = { page, limit: pageSize, sort: 'delivery_date', ...filterParams };
      const res = await api.get('/delivery-notes', { params });
      if (requestRef.current !== requestId) return;
      const data = res.data || {};
      const items = Array.isArray(data) ? data : (data.items || []);
      setState({
        items,
        total: data.total ?? items.length,
        totalPages: data.total_pages || 1,
        counts: data.counts || null,
        loading: false,
        error: '',
      });
    } catch (err) {
      if (requestRef.current !== requestId) return;
      setState((prev) => ({ ...prev, loading: false, error: getErrorMessage(err, 'Could not load delivery notes') }));
    }
  }, [page, pageSize, filterParams]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const projectOptions = useMemo(
    () => projects.map((project) => ({ value: project.id, label: project.name })).sort((a, b) => a.label.localeCompare(b.label)),
    [projects]
  );
  const filtered = Boolean(search || destination || projectId || supplierMissing);
  const clearFilters = () => { setSearchInput(''); setDestination(null); setProjectId(null); setSupplierMissing(false); };
  const counts = state.counts;
  const scopeTotal = counts?.all ?? state.total;
  const missing = counts?.supplier_missing ?? 0;
  const sub = `${plural(scopeTotal, 'delivery note')} · newest first`;
  const firstShown = (page - 1) * pageSize + 1;

  const gapChip = missing > 0 ? (
    <button
      type="button"
      aria-pressed={supplierMissing}
      onClick={() => setSupplierMissing((value) => !value)}
      title={supplierMissing ? 'Show all delivery notes' : 'Show only delivery notes without a supplier'}
      data-testid="dn-supplier-missing-chip"
      className={cn(
        'inline-flex h-[26px] items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 text-[12px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        supplierMissing
          ? 'border-[color:var(--status-amber)] bg-status-amber-bg text-status-amber-ink'
          : 'border-[color:var(--status-amber-bg)] bg-status-amber-bg text-status-amber-ink hover:border-[color:var(--status-amber)]'
      )}
    >
      <TriangleAlert className="h-[13px] w-[13px]" aria-hidden="true" />
      Supplier missing on {formatNumber(missing)} of {formatNumber(scopeTotal)}
      {supplierMissing ? <span className="ml-0.5 underline">Show all</span> : null}
    </button>
  ) : null;

  const tools = (
    <>
      <SearchField value={searchInput} onChange={setSearchInput} placeholder="Search DN no." label="Search delivery notes by number" className="w-full sm:w-[200px]" testId="dn-search" />
      <ChipSelect label="Destination" value={destination} options={DESTINATIONS} onChange={setDestination} testId="dn-filter-destination" align="end" />
      <ChipSelect label="Project" value={projectId} options={projectOptions} onChange={setProjectId} testId="dn-filter-project" align="end" emptyText="No projects" />
      {variant === 'recent' && onViewAll ? <Button variant="outline" size="sm" onClick={onViewAll} data-testid="dn-view-all">View all</Button> : null}
    </>
  );

  const rowActions = (dn) => (
    <RowActions
      label={`Actions for delivery note ${dn.dn_number}`}
      items={[
        { label: openId === dn.id ? 'Hide items' : 'View items', icon: ListTree, onSelect: () => setOpenId((id) => (id === dn.id ? null : dn.id)) },
        dn.project_id && { label: 'Open project', icon: FolderOpen, onSelect: () => navigate(`/projects/${dn.project_id}`) },
      ]}
    />
  );

  let body;
  const failed = Boolean(state.error);
  if (failed) {
    body = (
      <EmptyState layout="inline" tone="red" icon={CloudOff} title="Couldn't load delivery notes" text={state.error}
        action={<Button variant="outline" size="sm" onClick={load}>Try again</Button>} />
    );
  } else if (!state.loading && !state.items.length) {
    body = filtered ? (
      <EmptyState icon={SearchX} title="No delivery notes match" text="Try another DN number or remove a filter."
        action={<Button variant="outline" size="sm" onClick={clearFilters}>Clear filters</Button>} />
    ) : (
      <EmptyState icon={Truck} title="No deliveries recorded yet" text="Use Receive delivery when material arrives." />
    );
  } else if (isPhone) {
    body = (
      <ul className="divide-y divide-line-2">
        {state.items.map((dn) => {
          const toSite = dn.delivery_type === 'to_site';
          const Icon = toSite ? HardHat : Warehouse;
          const count = Array.isArray(dn.line_items) ? dn.line_items.length : 0;
          return (
            <li key={dn.id}>
              <button type="button" onClick={() => setOpenId((id) => (id === dn.id ? null : dn.id))} aria-expanded={openId === dn.id}
                className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-surface-hover">
                <span className="ui-icon-tile tone-gray h-9 w-9 shrink-0 rounded-lg" aria-hidden="true"><Icon className="h-4 w-4" /></span>
                <span className="min-w-0 flex-1">
                  <span className="flex min-w-0 items-baseline gap-2">
                    <span className="font-mono text-[13px] font-semibold text-ink">{dn.dn_number}</span>
                    <span className="truncate text-[13px] text-ink">{dn.project_name || 'No project'}</span>
                  </span>
                  <span className="block truncate text-[12px] text-ink-3">
                    {dn.delivery_date ? formatDate(dn.delivery_date, { year: 'auto' }) : 'No date'} · {toSite ? 'Direct to site' : `Warehouse${dn.destination_location ? ` (${dn.destination_location})` : ''}`}
                  </span>
                </span>
                <span className="shrink-0 text-[12.5px] tabular-nums text-ink-2">{plural(count, 'item')}</span>
              </button>
              {openId === dn.id ? <DnItems dn={dn} /> : null}
            </li>
          );
        })}
      </ul>
    );
  } else {
    body = (
      <TableScroll>
        <table className={cn(tableClass, 'min-w-[860px] table-fixed')}>
          <colgroup>
            <col style={{ width: 120 }} /><col style={{ width: 140 }} /><col /><col style={{ width: 170 }} />
            <col style={{ width: 140 }} /><col style={{ width: 170 }} /><col style={{ width: 90 }} /><col style={{ width: 56 }} />
          </colgroup>
          <thead>
            <tr>
              <th className={thClass}>DN</th>
              <th className={thClass}>Delivery date</th>
              <th className={thClass}>Project</th>
              <th className={thClass}>Supplier</th>
              <th className={thClass}>LPO</th>
              <th className={thClass}>Destination</th>
              <th className={cn(thClass, 'text-right')}>Items</th>
              <th className={thClass}><span className="sr-only">Actions</span></th>
            </tr>
          </thead>
          <tbody className={cn(state.loading && 'opacity-60 transition-opacity')}>
            {state.items.map((dn) => {
              const open = openId === dn.id;
              const count = Array.isArray(dn.line_items) ? dn.line_items.length : 0;
              return (
                <Fragment key={dn.id}>
                  <tr
                    className={cn('group cursor-pointer hover:bg-surface-hover', open && 'bg-brand-soft')}
                    onClick={() => setOpenId((id) => (id === dn.id ? null : dn.id))}
                    data-testid={`dn-row-${dn.id}`}
                  >
                    <td className={tdClass}>
                      <span className="inline-flex items-center gap-1.5">
                        {open ? <ChevronDown className="h-3.5 w-3.5 text-ink-3" aria-hidden="true" /> : <ChevronRight className="h-3.5 w-3.5 text-ink-4 group-hover:text-ink-3" aria-hidden="true" />}
                        <button
                          type="button"
                          aria-expanded={open}
                          aria-label={`${open ? 'Hide' : 'Show'} items of delivery note ${dn.dn_number}`}
                          onClick={(event) => { event.stopPropagation(); setOpenId((id) => (id === dn.id ? null : dn.id)); }}
                          className="truncate rounded font-mono text-[12.5px] font-semibold text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                          {dn.dn_number}
                        </button>
                      </span>
                    </td>
                    <td className={tdClass}><DateCell value={dn.delivery_date} mode="ago" emptyLabel="No date" /></td>
                    <td className={cn(tdClass, 'truncate font-semibold')}>{dn.project_name || <Dash />}</td>
                    <td className={cn(tdClass, 'truncate')}>{dn.supplier ? dn.supplier : <Dash label="No supplier" />}</td>
                    <td className={cn(tdClass, 'truncate')}>{dn.lpo_number ? <span className="font-mono text-[12.5px]">{dn.lpo_number}</span> : <Dash label="No LPO" />}</td>
                    <td className={cn(tdClass, 'py-1.5')}><DestinationTag type={dn.delivery_type} location={dn.destination_location} showLocation /></td>
                    <td className={cn(tdClass, numClass, 'whitespace-nowrap')}>{plural(count, 'item')}</td>
                    <td className={cn(tdClass, 'text-right')} onClick={(event) => event.stopPropagation()}>{rowActions(dn)}</td>
                  </tr>
                  {open ? (
                    <tr><td colSpan={8} className="border-b border-line-2 p-0"><DnItems dn={dn} /></td></tr>
                  ) : null}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </TableScroll>
    );
  }

  return (
    <SfCard data-testid={testId}>
      <CardHead
        title={title}
        sub={sub}
        right={isPhone ? null : tools}
      >
        {gapChip}
      </CardHead>
      {isPhone ? <div className="flex flex-wrap items-center gap-2 border-b border-line-2 px-4 py-2.5">{tools}</div> : null}
      {body}
      {!failed && state.items.length ? (
        <CardFoot>
          <span>
            Showing {formatNumber(firstShown)}–{formatNumber(firstShown + state.items.length - 1)} of {plural(state.total, 'delivery note')}
            {filtered && state.total !== scopeTotal ? <span> · filtered from {formatNumber(scopeTotal)}</span> : null}
          </span>
          <Pager page={page} pageCount={state.totalPages} onPage={setPage} label="Delivery note pages" />
        </CardFoot>
      ) : null}
    </SfCard>
  );
}
